"use client";

import type { Document } from "@contentful/rich-text-types";
import { RichTextRenderer } from "@scott/contentful/rich-text-renderer";
import type {
  ContentfulHeadlineBlock,
  ContentfulTextBlock,
} from "@scott/contentful/types";
import { cva, type VariantProps } from "class-variance-authority";
import { useState } from "react";
import { TextD2C } from "../../elements/text-d2c/text";
import { textD2CSizes } from "../../elements/text-d2c/text-d2c.constants";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import { Modal } from "../modal/modal";
import type { TextSectionInspectorProps } from "./text-section.preview";

const WORD_BOUNDARY_MIN_RATIO = 0.75;

type TextSectionTruncationMode = "expand" | "modal";

interface RichTextNode {
  nodeType: string;
  data?: unknown;
  value?: string;
  marks?: unknown[];
  content?: RichTextNode[];
}

export const textSectionVariants = cva("", {
  variants: {
    columnWidth: {
      8: "col-span-10 col-start-2 md:col-span-8 md:col-start-3",
      10: "col-span-10 col-start-2",
      12: "col-span-12",
    },
    textColumns: {
      1: "columns-1 gap-unit",
      2: "columns-1 gap-unit md:columns-2",
      3: "columns-1 gap-unit md:columns-3",
      4: "columns-2 gap-unit md:columns-4",
    },
    textVariant: {
      "p-xlarge": "",
      "p-large": "",
      p: "",
      "p-small": "",
    },
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
    color: {
      black: "text-black",
      white: "text-white",
      alabastergrey: "text-alabastergrey",
    },
  },
  defaultVariants: {
    columnWidth: 10,
    textColumns: 1,
    textVariant: "p",
    backgroundColor: "white",
    color: "black",
  },
});

export interface TextSectionProps
  extends VariantProps<typeof textSectionVariants> {
  sys?: { id: string };
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  columnWidth?: VariantProps<typeof textSectionVariants>["columnWidth"];
  textColumns?: VariantProps<typeof textSectionVariants>["textColumns"];
  textVariant?: VariantProps<typeof textSectionVariants>["textVariant"];
  backgroundColor?: "black" | "white" | "whitesmoke";
  headlineColor?: "black" | "white" | "alabastergrey";
  color?: "black" | "white" | "alabastergrey";
  headline?: ContentfulHeadlineBlock;
  text?: ContentfulTextBlock;
  headlineVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  headlineDash?: boolean;
  anchorId?: string;
  outerContainerOnly?: boolean;
  truncateAt?: number;
  truncationMode?: TextSectionTruncationMode;
  readMoreLabel?: string;
  truncationModalTitle?: string;
  inspectorProps?: TextSectionInspectorProps;
}

function extractRichTextValue(node: RichTextNode): string {
  const value = typeof node.value === "string" ? node.value : "";
  const childValue = Array.isArray(node.content)
    ? node.content.map(extractRichTextValue).join("")
    : "";

  return `${value}${childValue}`;
}

export function getRichTextPlainText(document?: Document): string {
  if (!document) {
    return "";
  }

  return extractRichTextValue(document as unknown as RichTextNode);
}

function truncateTextValue(value: string, truncateAt: number): string {
  if (value.length <= truncateAt) {
    return value;
  }

  const clipped = value.slice(0, truncateAt).trimEnd();
  const lastWhitespaceIndex = clipped.search(/\s+\S*$/);
  const minimumWordBoundaryIndex = Math.floor(
    truncateAt * WORD_BOUNDARY_MIN_RATIO
  );

  if (lastWhitespaceIndex > minimumWordBoundaryIndex) {
    return clipped.slice(0, lastWhitespaceIndex).trimEnd();
  }

  return clipped;
}

export function truncateRichTextDocument(
  document: Document | undefined,
  truncateAt: number
): Document | undefined {
  if (!(document && Number.isFinite(truncateAt) && truncateAt > 0)) {
    return document;
  }

  let remainingCharacters = truncateAt;

  function truncateNode(node: RichTextNode): RichTextNode | null {
    if (remainingCharacters <= 0) {
      return null;
    }

    if (typeof node.value === "string") {
      if (node.value.length <= remainingCharacters) {
        remainingCharacters -= node.value.length;
        return { ...node };
      }

      const value = truncateTextValue(node.value, remainingCharacters);
      remainingCharacters = 0;

      return {
        ...node,
        value,
      };
    }

    if (Array.isArray(node.content)) {
      const content: RichTextNode[] = [];

      for (const child of node.content) {
        const truncatedChild = truncateNode(child);

        if (truncatedChild) {
          content.push(truncatedChild);
        }

        if (remainingCharacters <= 0) {
          break;
        }
      }

      return {
        ...node,
        content,
      };
    }

    return { ...node };
  }

  return truncateNode(document as unknown as RichTextNode) as unknown as
    | Document
    | undefined;
}

export function TextSection({
  columnWidth,
  textColumns,
  textVariant,
  headline,
  headlineVariant = "h3",
  headlineDash = true,
  text,
  backgroundColor,
  headlineColor,
  color,
  outerContainerOnly,
  truncateAt,
  truncationMode = "expand",
  readMoreLabel,
  truncationModalTitle,
  inspectorProps,
  ...props
}: Readonly<TextSectionProps>) {
  const [state, setState] = useState({
    isExpanded: false,
    isModalOpen: false,
  });

  const textDocument = text?.text?.json;
  const resolvedTextVariant = textVariant ?? "p";
  const headlineText = getRichTextPlainText(headline?.headline?.json);
  const textPlainText = getRichTextPlainText(textDocument);
  const shouldTruncate =
    typeof truncateAt === "number" &&
    Number.isFinite(truncateAt) &&
    truncateAt > 0 &&
    textPlainText.length > truncateAt;
  const truncatedTextDocument = shouldTruncate
    ? truncateRichTextDocument(textDocument, truncateAt)
    : textDocument;
  const modalTitle = truncationModalTitle || headlineText;
  const canShowReadMore =
    shouldTruncate &&
    Boolean(readMoreLabel) &&
    (truncationMode === "expand" || Boolean(modalTitle));
  const renderedTextDocument =
    canShowReadMore && !state.isExpanded ? truncatedTextDocument : textDocument;

  const containerClasses = textSectionVariants({
    columnWidth: null,
    textColumns: null,
    textVariant: null,
    backgroundColor,
    color,
  });

  const columnClasses = textSectionVariants({
    columnWidth,
    textColumns: null,
    textVariant: null,
    backgroundColor: null,
    color: null,
  });

  const columnTextClasses = textSectionVariants({
    columnWidth: null,
    textColumns,
    textVariant: null,
    backgroundColor: null,
    color: null,
  });
  const readMoreButtonClasses = cn(
    textSectionVariants({
      columnWidth: null,
      textColumns: null,
      textVariant: null,
      backgroundColor: null,
      color,
    }),
    textD2CSizes[resolvedTextVariant],
    "mt-1 inline cursor-pointer bg-transparent p-0 text-left font-brand underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
  );

  const handleReadMoreClick = () => {
    if (truncationMode === "modal") {
      setState((currentState) => ({
        ...currentState,
        isModalOpen: true,
      }));
      return;
    }

    setState((currentState) => ({
      ...currentState,
      isExpanded: true,
    }));
  };

  return (
    <>
      <Container
        className={cn(containerClasses, "component-text-section -mb-px")}
        fullWidth={true}
        id={props.anchorId}
        outerContainerOnly={outerContainerOnly}
        paddingBottom={props.paddingBottom}
        paddingTop={props.paddingTop}
        testId="text-section"
        {...inspectorProps?.container}
      >
        <Row className="gap-0">
          <Column className={cn(columnClasses)}>
            {headline && (
              <TextD2C
                color={headlineColor}
                dash={headlineDash}
                variant={headlineVariant}
                {...inspectorProps?.headline}
              >
                <RichTextRenderer richText={headline.headline?.json} />
              </TextD2C>
            )}
            {textDocument && (
              <div className={columnTextClasses}>
                <TextD2C
                  color={color}
                  variant={resolvedTextVariant}
                  {...inspectorProps?.text}
                >
                  <RichTextRenderer richText={renderedTextDocument} />
                </TextD2C>
                {canShowReadMore && !state.isExpanded ? (
                  <button
                    aria-expanded={
                      truncationMode === "expand" ? state.isExpanded : undefined
                    }
                    aria-haspopup={
                      truncationMode === "modal" ? "dialog" : undefined
                    }
                    className={readMoreButtonClasses}
                    onClick={handleReadMoreClick}
                    type="button"
                  >
                    {readMoreLabel}
                  </button>
                ) : null}
              </div>
            )}
          </Column>
        </Row>
      </Container>
      {canShowReadMore && truncationMode === "modal" ? (
        <Modal
          headerCentered={false}
          isOpen={state.isModalOpen}
          modalClassName="max-w-2xl p-6 md:max-w-3xl"
          onClose={() =>
            setState((currentState) => ({
              ...currentState,
              isModalOpen: false,
            }))
          }
          title={modalTitle}
          titleAs="h5"
          titleClassName="text-base font-semibold leading-8 tracking-normal"
        >
          <TextD2C
            color="black"
            variant={resolvedTextVariant}
            {...inspectorProps?.text}
          >
            <RichTextRenderer richText={textDocument} />
          </TextD2C>
        </Modal>
      ) : null}
    </>
  );
}
